import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { t } from "@/i18n";
import { Calendar, Users, Heart, Sparkles, Briefcase, PartyPopper, Utensils, Check, ArrowLeft, ArrowRight } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/4ab3c465-caf6-4699-80b7-ac0418042b65/images/65b553d9446bb8b6de573ff329266334a0fd099a181225ed85067687e1c47c10.png";

const OCCASIONS = [
  { key: "dinner", icon: Utensils, color: "text-[#F5F5F0]" },
  { key: "romantic", icon: Heart, color: "text-rose-300" },
  { key: "birthday", icon: PartyPopper, color: "text-gold" },
  { key: "business", icon: Briefcase, color: "text-blue-300" },
  { key: "family", icon: Users, color: "text-emerald-300" },
  { key: "event", icon: Sparkles, color: "text-purple-300" },
];

const STEPS = ["step_when", "step_who", "step_details", "step_confirm"];

export default function PublicBooking() {
  const { slug } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [serviceId, setServiceId] = useState(null);
  const [party, setParty] = useState(2);
  const [occasion, setOccasion] = useState("dinner");
  const [allergies, setAllergies] = useState("");
  const [notes, setNotes] = useState("");
  const [guest, setGuest] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/restaurants/${slug}`);
        setRestaurant(data.restaurant);
        setServices(data.services);
      } catch {
        setError("Restaurant nicht gefunden.");
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!restaurant) return;
    (async () => {
      try {
        const { data } = await api.get(`/public/restaurants/${slug}/availability`, { params: { date } });
        setSlots(data.slots);
      } catch { setSlots([]); }
    })();
  }, [date, restaurant, slug]);

  const groupedSlots = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      if (!map[s.service_name]) map[s.service_name] = [];
      map[s.service_name].push(s);
    });
    return map;
  }, [slots]);

  const canNext = () => {
    if (step === 0) return !!time && !!date;
    if (step === 1) return party > 0 && !!occasion;
    if (step === 2) return guest.name.trim() && guest.email.trim();
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const allergyList = allergies.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        guest_name: guest.name,
        guest_email: guest.email,
        guest_phone: guest.phone,
        party_size: party,
        date,
        time,
        reservation_type: occasion,
        allergies: allergyList,
        notes,
        service_id: serviceId,
      };
      const { data } = await api.post(`/public/restaurants/${slug}/reservations`, payload);
      setSuccess(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Etwas ist schiefgelaufen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#F5F5F0]">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-light mb-2">404</h1>
          <p className="text-sm text-[#A3A39F]">{error}</p>
        </div>
      </div>
    );
  }
  if (!restaurant) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-sm text-[#A3A39F]">{t("common.loading")}</div>;

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/30 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        </div>
        <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-16">
          <div className="max-w-lg w-full surface-1 rounded-md p-10 text-center animate-fade-up" data-testid="booking-success">
            <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center mx-auto mb-6">
              <Check className="w-6 h-6 text-gold" strokeWidth={1.5} />
            </div>
            <StepTitle>{restaurant.name}</StepTitle>
            <h1 className="font-heading text-3xl font-light mb-3">{t("booking.success_title")}</h1>
            <p className="text-sm text-[#A3A39F] mb-8">{t("booking.success_text")}</p>
            <div className="surface-2 rounded-md p-5 text-left text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-[#A3A39F]">{t("common.date")}</span><span>{date}</span></div>
              <div className="flex justify-between"><span className="text-[#A3A39F]">{t("common.time")}</span><span className="text-gold font-heading">{time}</span></div>
              <div className="flex justify-between"><span className="text-[#A3A39F]">{t("common.guests")}</span><span>{party}</span></div>
              <div className="flex justify-between"><span className="text-[#A3A39F]">{t("reservations.type")}</span><span>{t(`reservations.types.${occasion}`)}</span></div>
            </div>
            <div className="mt-10 flex flex-col items-center gap-2">
              <div className="font-heading font-extralight text-[1.05rem] tracking-[0.55em] text-[var(--text-primary)]">SONARA</div>
              <div className="text-[10px] text-[var(--text-dim)] tracking-[0.3em] uppercase">{t("booking.powered_by")}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] relative">
      {/* Hero */}
      <div className="relative h-[40vh] min-h-[280px] overflow-hidden">
        <img src={HERO_IMG} alt={restaurant.name} className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/60 to-[#0A0A0A]" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <div className="w-full max-w-md mb-3">
            <div className="flex items-center gap-3">
              <span className="flex-1 h-px bg-gold/50" aria-hidden />
              <span className="text-[10px] uppercase tracking-[0.32em] text-gold whitespace-nowrap">
                {restaurant.tagline || "Restaurant & Lounge"}
              </span>
              <span className="flex-1 h-px bg-gold/50" aria-hidden />
            </div>
          </div>
          {restaurant.logo_url && (
            <img
              src={restaurant.logo_url}
              alt={`${restaurant.name} Logo`}
              data-testid="booking-restaurant-logo"
              className="max-h-20 sm:max-h-24 mb-4 object-contain"
            />
          )}
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extralight tracking-tighter mb-3" data-testid="booking-restaurant-name">{restaurant.name}</h1>
          {restaurant.address && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${restaurant.name} ${restaurant.address}`)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="booking-address-link"
              className="text-sm text-[#A3A39F] hover:text-gold transition-colors max-w-md underline-offset-4 hover:underline"
            >
              {restaurant.address}
            </a>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12 lg:py-16">
        {/* Steps progress */}
        <div className="flex items-center justify-between mb-10">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${i === step ? "text-gold" : i < step ? "text-[#F5F5F0]" : "text-[#6B6B66]"}`}>
                <div className={`w-7 h-7 rounded-full border flex items-center justify-center text-xs font-heading ${i === step ? "border-gold bg-gold/10" : i < step ? "border-[#F5F5F0]/30" : "border-white/10"}`}>
                  {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className="hidden sm:inline text-[10px] uppercase tracking-wider">{t(`booking.${s}`)}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-[#F5F5F0]/30" : "bg-white/10"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="surface-1 rounded-md p-6 sm:p-10 animate-fade-up">
          {/* STEP 0: when */}
          {step === 0 && (
            <div data-testid="booking-step-when">
              <StepTitle>{t("booking.step_when")}</StepTitle>
              <h2 className="font-heading text-2xl font-light mb-8">{t("booking.title")}</h2>
              <label className="block mb-6">
                <div className="text-xs text-[#A3A39F] mb-2 uppercase tracking-wider">{t("booking.select_date")}</div>
                <input
                  type="date"
                  value={date}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => { setDate(e.target.value); setTime(""); }}
                  data-testid="booking-date-input"
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-md h-12 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-gold [color-scheme:dark]"
                />
              </label>

              <div className="text-xs text-[#A3A39F] mb-3 uppercase tracking-wider">{t("booking.select_time")}</div>
              {Object.keys(groupedSlots).length === 0 && (
                <div className="text-sm text-[#6B6B66] py-8 text-center">{t("booking.no_slots")}</div>
              )}
              {Object.entries(groupedSlots).map(([service, list]) => (
                <div key={service} className="mb-6">
                  <div className="text-[10px] text-gold mb-2 tracking-wider uppercase">{service}</div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {list.map((s) => (
                      <button
                        key={`${s.service_id}-${s.time}`}
                        onClick={() => { setTime(s.time); setServiceId(s.service_id); }}
                        data-testid={`booking-slot-${s.time}`}
                        className={`h-10 rounded text-sm font-heading transition-all ${time === s.time ? "bg-gold text-black" : "bg-white/5 text-[#F5F5F0] hover:bg-white/10"}`}
                      >
                        {s.time}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* STEP 1: who */}
          {step === 1 && (
            <div data-testid="booking-step-who">
              <StepTitle>{t("booking.step_who")}</StepTitle>
              <h2 className="font-heading text-2xl font-light mb-8">Gäste & Anlass</h2>
              <div className="text-xs text-[#A3A39F] mb-3 uppercase tracking-wider">{t("booking.party_size")}</div>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mb-8">
                {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                  <button
                    key={n}
                    onClick={() => setParty(n)}
                    data-testid={`booking-party-${n}`}
                    className={`h-12 rounded font-heading text-lg transition-all ${party === n ? "bg-gold text-black" : "bg-white/5 hover:bg-white/10"}`}
                  >{n}</button>
                ))}
              </div>
              <div className="text-xs text-[#A3A39F] mb-3 uppercase tracking-wider">{t("booking.occasion")}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {OCCASIONS.map((o) => (
                  <button
                    key={o.key}
                    onClick={() => setOccasion(o.key)}
                    data-testid={`booking-occasion-${o.key}`}
                    className={`p-4 rounded border text-left transition-all ${occasion === o.key ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/20"}`}
                  >
                    <o.icon className={`w-5 h-5 mb-2 ${o.color}`} strokeWidth={1.5} />
                    <div className="text-sm">{t(`reservations.types.${o.key}`)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: details */}
          {step === 2 && (
            <div data-testid="booking-step-details">
              <StepTitle>{t("booking.step_details")}</StepTitle>
              <h2 className="font-heading text-2xl font-light mb-8">{t("booking.contact")}</h2>
              <div className="space-y-4">
                <label className="block">
                  <div className="text-xs text-[#A3A39F] mb-2 uppercase tracking-wider">{t("booking.full_name")}</div>
                  <input value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} data-testid="booking-name-input" className="w-full bg-[#0A0A0A] border border-white/10 rounded-md h-12 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-gold" />
                </label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <div className="text-xs text-[#A3A39F] mb-2 uppercase tracking-wider">{t("common.email")}</div>
                    <input type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} data-testid="booking-email-input" className="w-full bg-[#0A0A0A] border border-white/10 rounded-md h-12 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-gold" />
                  </label>
                  <label className="block">
                    <div className="text-xs text-[#A3A39F] mb-2 uppercase tracking-wider">{t("common.phone")} ({t("common.optional")})</div>
                    <input value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} data-testid="booking-phone-input" className="w-full bg-[#0A0A0A] border border-white/10 rounded-md h-12 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-gold" />
                  </label>
                </div>
                <label className="block">
                  <div className="text-xs text-[#A3A39F] mb-2 uppercase tracking-wider">{t("common.allergies")} ({t("common.optional")})</div>
                  <input value={allergies} onChange={(e) => setAllergies(e.target.value)} data-testid="booking-allergies-input" placeholder={t("booking.allergies_placeholder")} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md h-12 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-gold" />
                </label>
                <label className="block">
                  <div className="text-xs text-[#A3A39F] mb-2 uppercase tracking-wider">{t("common.notes")} ({t("common.optional")})</div>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="booking-notes-input" rows={3} placeholder={t("booking.notes_placeholder")} className="w-full bg-[#0A0A0A] border border-white/10 rounded-md py-3 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-gold" />
                </label>
              </div>
            </div>
          )}

          {/* STEP 3: confirm */}
          {step === 3 && (
            <div data-testid="booking-step-confirm">
              <StepTitle>{t("booking.step_confirm")}</StepTitle>
              <h2 className="font-heading text-2xl font-light mb-8">Bitte bestätigen</h2>
              <div className="space-y-3 text-sm">
                <Row label={t("common.date")} value={date} />
                <Row label={t("common.time")} value={time} gold />
                <Row label={t("common.guests")} value={party} />
                <Row label={t("reservations.type")} value={t(`reservations.types.${occasion}`)} />
                <Row label={t("common.name")} value={guest.name} />
                <Row label={t("common.email")} value={guest.email} />
                {guest.phone && <Row label={t("common.phone")} value={guest.phone} />}
                {allergies && <Row label={t("common.allergies")} value={allergies} />}
                {notes && <Row label={t("common.notes")} value={notes} />}
              </div>
              {error && <div className="mt-4 text-xs text-red-400" data-testid="booking-error">{error}</div>}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-white/5">
            <button
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              data-testid="booking-back-btn"
              className="btn-ghost text-xs inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {t("common.back")}
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => canNext() && setStep(step + 1)}
                disabled={!canNext()}
                data-testid="booking-next-btn"
                className="btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {t("common.next")} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="booking-submit-btn"
                className="btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-30"
              >
                {submitting ? t("common.loading") : t("booking.submit")}
              </button>
            )}
          </div>
        </div>

        <div className="mt-12 text-center flex flex-col items-center gap-2">
          <div className="font-heading font-extralight text-[1.1rem] tracking-[0.55em] text-[var(--text-primary)]">SONARA</div>
          <div className="text-[10px] text-[var(--text-dim)] tracking-[0.35em] uppercase">{t("booking.powered_by")}</div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, gold }) {
  return (
    <div className="flex justify-between border-b border-white/5 pb-3">
      <span className="text-[#A3A39F]">{label}</span>
      <span className={gold ? "text-gold font-heading" : ""}>{value}</span>
    </div>
  );
}

function StepTitle({ children }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className="text-[10px] uppercase tracking-[0.32em] text-gold whitespace-nowrap">
        {children}
      </span>
      <span className="flex-1 h-px bg-gold/50" aria-hidden />
    </div>
  );
}
